/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { Tab, Tabs } from "@mui/material";
import { styled } from "@mui/material/styles";
import * as React from "react";
import { ReactNode, useEffect } from "react";
import { NumberParam, StringParam, useQueryParam } from "use-query-params";

const CustomTabs = styled(Tabs)`
  & > .MuiTabs-scrollButtons.Mui-disabled {
    opacity: 0.3;
  }
`

type AlignItemDetailsHorizontal = 'left' | 'center' | 'right'

interface IHorizontalTabsProps {
  initContent: ReactNode
  initHtabIndex: number
  horizontalTabs: any[]
  align: AlignItemDetailsHorizontal
  favoriteType?: string
  uid: string
  outerDivProps? : any,
  padding? : any,
  contentDivProps?: any
  favIcon?: ReactNode
  tabNameUrlKey?: string
  createValueFromHtabName?: any
}

const NULL = 'null'

export const CoverageRequirementsHorizontalTabs = (props: IHorizontalTabsProps) => {
  const htabParamId = `${props.uid}`
  const [htabIndex, setHtabIndex] = useQueryParam(htabParamId, NumberParam)
  const [htab, setHtab] = useQueryParam(decodeURI(props?.tabNameUrlKey ?? `${htabParamId}-name`), StringParam)
  const decodedHtab = decodeURI(htab ?? NULL)
  useEffect(() => {
    /**
     * If loading from link then we want to use htab name (any time htab is defined initially, then use it). Otherwise,
     * use initial htab index, and set htab name to the name of the tab at that index. If it's coming from link and
     * the htab name is not a param, then we want to use the htab index in the url not the initial tab index.
     */
    if (decodedHtab != NULL && decodedHtab != null) {
      const _htabIndex = getTabIndexFromName(decodedHtab ?? '', props.initHtabIndex)
      setHtabIndex(_htabIndex)
    } else {
      if(htabIndex == null) {
        setHtabIndex(props.initHtabIndex)
        if(props.horizontalTabs?.length>0) setHtab(props.horizontalTabs[props.initHtabIndex]?.name ?? NULL)
        return
      }
      if(!(props.horizontalTabs?.length>0)){
        setHtabIndex(htabIndex)
        return
      }
      setHtab(props.horizontalTabs[htabIndex]?.name ?? NULL)
    }
  }, [props.horizontalTabs, props.initHtabIndex]);

  const changeHorizontal = (event: React.SyntheticEvent, newValue: number) => {
    setHtabIndex(newValue)
    setHtab(props.horizontalTabs[newValue]?.name ?? NULL)
  };

  const getTabIndexFromName = (name: string, defaultIndex: number) => {
    return props.horizontalTabs?.findIndex((x: any) => x?.name == name) ?? defaultIndex
  }

  return (
    <div
      style={{
        display: 'flex',
        flexFlow: 'column nowrap',
        flexGrow: 1,
        overflow: 'auto',
        height: '100%',
        padding: 10,
        ...props.padding}}>
      <div style={{
        padding: 5,
        display: props.horizontalTabs?.length ? 'flex' : 'none',
        flexShrink: 0,
        justifyContent: props.align,
        margin: 0,
        ...props.outerDivProps
      }}>
        <CustomTabs
          orientation="horizontal"
          variant="scrollable"
          // scrollButtons={true}
          allowScrollButtonsMobile
          value={htabIndex}
          onChange={changeHorizontal}
        >
          {props.horizontalTabs?.map((v: any, i: number) => {
            return (
              <Tab label={v?.name} key={i} iconPosition={'end'}/>
            )
          })}
        </CustomTabs>
        <div style={{alignSelf: 'center', marginLeft: 'auto'}}>{props?.favIcon}</div>
      </div>
      <div key={'htabs-container'} style={{
        flexGrow: 1, overflow: 'auto', margin: 20,
        ...props?.contentDivProps
      }}>
        {props.horizontalTabs.map((v: any, i: number) => {
          return (
            <div key={'htab' + i} style={{ display: htabIndex == i ? "" : "none", height: "100%" }}>
              {props.horizontalTabs?.[i]?.content}
            </div>
          )
        })
        }
      </div>
    </div>
  )
}